import React from 'react';
import { Section, Eyebrow, H2, Reveal, DataTable, Ref, FONT, MONO } from './ui';
import { VideoClip } from './VideoClip';
import { QueenCompare } from './QueenCompare';

const CaseHead = ({ c, L, t, dark }) => (
  <Reveal>
    <div className="flex items-start gap-6">
      <span className={`text-7xl sm:text-9xl font-extrabold leading-none ${dark ? 'text-blue-500/30' : 'text-blue-100'}`} style={FONT}>{c.n}</span>
      <div className="pt-2">
        <Eyebrow dark={dark}>{t.case} {Number(c.n)} · {L(c.eyebrow)}</Eyebrow>
        <H2 dark={dark} className="mt-3">{c.title}</H2>
        <p className={`mt-3 text-lg ${dark ? 'text-blue-100/75' : 'text-slate-600'}`}>{L(c.subtitle)}</p>
      </div>
    </div>
  </Reveal>
);

export const CaseTrump = ({ c, L, t, videos = [], slug }) => (
  <Section id="case-trump" dark>
    <CaseHead c={c} L={L} t={t} dark />
    <Reveal><p className="mt-8 max-w-3xl text-sm text-blue-100/60 border-l-2 border-blue-500/50 pl-4">{L(c.frame)}</p></Reveal>
    <Reveal><div className="mt-10"><p className="text-sm font-bold text-blue-200 mb-3" style={MONO}>{t.fourThings}</p>
      <DataTable dark cols={L(c.table.cols)} rows={c.table.rows} testid="trump-features-table" />
      <p className="mt-3 text-sm text-blue-100/70">{L(c.tableNote)}</p></div></Reveal>
    <div className="mt-14 grid lg:grid-cols-2 gap-8" data-testid="trump-case-card">
      <Reveal>
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 h-full">
          <p className="text-2xl font-extrabold" style={FONT}>{L(c.question.title)}</p>
          <p className="mt-2 text-3xl font-extrabold text-cyan-300" style={FONT}>{L(c.question.answer)}</p>
          {L(c.question.points).map((p) => <p key={p} className="mt-4 text-blue-100/80">{p}</p>)}
          <div className="mt-6 space-y-2" data-testid="labov-stores">
            {c.question.stores.map((s) => (
              <div key={s.name} className="flex items-center gap-3">
                <span className="w-20 text-sm font-bold">{s.name}</span>
                <div className="flex-1 h-3 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-300" style={{ width: `${s.level * 31}%` }} /></div>
                <span className="w-28 text-xs text-blue-200/70">{L(s.rank)}</span>
              </div>
            ))}
            <p className="text-[11px] text-blue-200/50 pt-1">“fourth floor” · /r/ — {t.relative}</p>
          </div>
          <div className="mt-5"><Ref dark>{c.question.ref}</Ref></div>
        </div>
      </Reveal>
      <Reveal delay={120}>
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-blue-800 p-8 h-full flex flex-col">
          <p className="text-[11px] uppercase tracking-[0.2em] text-blue-100" style={MONO}>{t.reading}</p>
          <p className="mt-4 text-xl font-semibold leading-relaxed">{L(c.question.reading)}</p>
        </div>
      </Reveal>
    </div>
    <Reveal>
      <div className="mt-14 rounded-3xl border border-amber-400/40 bg-amber-400/[0.06] p-8" data-testid="trump-activity">
        <div className="flex flex-wrap items-baseline gap-3"><span className="px-3 py-1 rounded-full bg-amber-400 text-slate-950 text-xs font-bold uppercase tracking-widest">{t.activity} · {c.activity.minutes} min</span><p className="text-2xl font-extrabold" style={FONT}>{L(c.activity.title)}</p></div>
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {c.activity.clips.map((k, i) => (videos[i] ? <div key={k.label}><VideoClip v={videos[i]} t={t} L={L} slug={slug} pron={videos[i].id.startsWith('trump') ? c.pron : undefined} pronNote={c.pronNote && L(c.pronNote)} pronLabels={c.pronLabels && L(c.pronLabels)} labDialect="us" /><p className="mt-1 text-sm text-blue-100/70">{L(k.desc)}</p></div> : <div key={k.label} className="rounded-2xl bg-white/5 border border-white/10 p-5"><p className="font-bold">{k.label}</p><p className="text-sm text-blue-100/70">{L(k.desc)}</p></div>))}
        </div>
        <ol className="mt-6 space-y-2">{L(c.activity.steps).map((s, i) => <li key={s} className="flex gap-3"><span className="text-amber-300 font-bold" style={MONO}>{i + 1}.</span><span className="text-blue-50">{s}</span></li>)}</ol>
        <p className="mt-6 text-2xl font-extrabold text-amber-300" style={FONT}>{L(c.activity.lesson)}</p>
      </div>
    </Reveal>
  </Section>
);

export const CaseQueen = ({ c, L, t, videos = [] }) => (
  <Section id="case-queen">
    <CaseHead c={c} L={L} t={t} />
    <div className="mt-12 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 items-start" data-testid="queen-case-card">
      <Reveal>
        <div className="rounded-3xl bg-slate-900 text-white p-8">
          <p className="text-2xl font-extrabold" style={FONT}>{L(c.evidence.title)}</p>
          <div className="mt-6 flex items-center gap-3" aria-hidden="true">
            <span className="px-3 py-1 rounded-lg bg-white/10 font-bold" style={MONO}>{c.evidence.from}</span>
            <span className="flex-1 h-1 rounded-full bg-gradient-to-r from-amber-300 via-blue-400 to-cyan-300" />
            <span className="px-3 py-1 rounded-lg bg-white/10 font-bold" style={MONO}>{c.evidence.to}</span>
          </div>
          <p className="mt-6 text-blue-100/85">{L(c.evidence.body)}</p>
          <p className="mt-4 text-sm text-amber-200">{L(c.evidence.listen)}</p>
          <div className="mt-5"><Ref dark>{c.evidence.ref}</Ref></div>
        </div>
      </Reveal>
      <Reveal delay={120}>
        <DataTable cols={L(c.table.cols)} rows={c.table.rows} testid="queen-rp-table" />
        <p className="mt-3 text-sm text-slate-500">{L(c.tableNote)}</p>
      </Reveal>
    </div>
    {!!videos.length && <Reveal><QueenCompare videos={videos} t={t} L={L} pron={c.pron} pronNote={c.pronNote && L(c.pronNote)} pronLabels={c.pronLabels && L(c.pronLabels)} labDialect="rp" /></Reveal>}
    <Reveal>
      <p className="mt-16 text-sm font-bold text-slate-500" style={MONO}>{t.whyChanged}</p>
      <div className="mt-6 grid sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="queen-timeline">
        {c.timeline.map((x, i) => (
          <div key={x.year} className="relative rounded-2xl border border-slate-200 p-5 bg-white hover:border-blue-400 transition-colors">
            <span className="absolute top-0 left-5 right-5 h-1 rounded-b bg-blue-600" style={{ opacity: 0.35 + i * 0.13 }} />
            <p className="text-2xl font-extrabold text-blue-700" style={FONT}>{x.year}</p>
            <p className="mt-2 text-sm text-slate-600">{L(x.text)}</p>
          </div>
        ))}
      </div>
      <p className="mt-4 text-sm text-slate-500">{L(c.timelineNote)}</p>
    </Reveal>
    <Reveal>
      <div className="mt-16 rounded-[2rem] bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 text-white p-10 lg:p-14" data-testid="queen-lesson">
        <p className="text-[11px] uppercase tracking-[0.25em] text-blue-100" style={MONO}>{t.lessonFor}</p>
        <p className="mt-4 text-3xl lg:text-4xl font-extrabold leading-tight max-w-4xl" style={FONT}>{L(c.lesson.title)}</p>
        <p className="mt-4 text-lg text-blue-50/90">{L(c.lesson.body)}</p>
      </div>
    </Reveal>
  </Section>
);

export const CaseBoardroom = ({ c, L, t }) => (
  <Section id="case-boardroom" className="bg-slate-50">
    <CaseHead c={c} L={L} t={t} />
    <Reveal><p className="mt-12 text-xl font-bold" style={FONT}>{L(c.evidence.title)}</p></Reveal>
    <div className="mt-6 grid md:grid-cols-3 gap-5" data-testid="pitch-boardroom-card">
      {c.evidence.cols.map((x, i) => (
        <Reveal key={i} delay={i * 100}>
          <div className="rounded-3xl bg-white border border-slate-200 p-7 h-full flex flex-col">
            <p className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold" style={MONO}>{L(x.key)}</p>
            <p className="mt-3 text-slate-700 flex-1">{L(x.body)}</p>
            <p className="mt-4 text-xs italic text-slate-500">{x.ref}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal><p className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">{L(c.evidence.caveat)}</p></Reveal>
    <div className="mt-14 grid lg:grid-cols-2 gap-8">
      <Reveal>
        <div className="rounded-3xl bg-[#060d24] text-white p-8 h-full" data-testid="boardroom-design">
          <p className="text-2xl font-extrabold" style={FONT}>{L(c.design.title)}</p>
          <p className="mt-4 text-blue-100/80">{L(c.design.body)}</p>
          <p className="mt-6 text-lg font-bold text-amber-300">{L(c.design.lesson)}</p>
          <div className="mt-5"><Ref dark>{c.design.ref}</Ref></div>
        </div>
      </Reveal>
      <Reveal delay={120}>
        <div data-testid="boardroom-moves">
          <p className="text-2xl font-extrabold" style={FONT}>{L(c.moves.title)}</p>
          <p className="text-xs text-slate-500 mt-1">{L(c.moves.note)}</p>
          <div className="mt-5 space-y-3">
            {c.moves.items.map((m, i) => (
              <div key={i} className="flex gap-5 rounded-2xl bg-white border border-slate-200 p-5 hover:border-blue-400 transition-colors">
                <span className="text-4xl font-extrabold text-blue-600" style={FONT}>{i + 1}</span>
                <div><p className="font-bold text-lg">{L(m.title)}</p><p className="text-slate-600 text-sm mt-1">{L(m.body)}</p></div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </Section>
);
